import { useCallback } from 'react';
import { api } from '@/api/client';
import { useCachedResource, type CachedResource } from '@/api/useCachedResource';
import type { Invoice, PaymentMethod, Subscription } from './types';

/**
 * Data layer for the Billing tab, backed by the module-level cache
 * (useCachedResource): each resource loads once per page session and is served
 * instantly on subsequent mounts, so switching Settings tabs never refetches.
 * It only re-fetches on an explicit refetch — `refetchAll()` after a plan
 * cancel/select, the per-card retry buttons, or a full page reload.
 *
 * userId is NOT passed from the client — every endpoint derives it from the JWT
 * server-side, so the browser can only read the signed-in user's own billing.
 */

export interface BillingData {
  subscription: CachedResource<Subscription | null>;
  invoices: CachedResource<Invoice[]>;
  paymentMethod: CachedResource<PaymentMethod | null>;
  refetchAll(): void;
}

export function useBillingData(enabled: boolean): BillingData {
  // Stable fetchers (no deps) — required by useCachedResource.
  const fetchSubscription = useCallback(
    () => api.get<{ subscription: Subscription | null }>('/api/v2/subscriptions/me').then((r) => r.subscription),
    [],
  );
  const fetchInvoices = useCallback(
    () => api.get<{ invoices: Invoice[] }>('/api/v2/billing/history?limit=10').then((r) => r.invoices ?? []),
    [],
  );
  const fetchPaymentMethod = useCallback(
    () => api.get<{ paymentMethod: PaymentMethod }>('/api/v2/billing/payment-method').then((r) => r.paymentMethod),
    [],
  );

  const subscription = useCachedResource('subscriptions/me', fetchSubscription, enabled);
  const invoices = useCachedResource('billing/history', fetchInvoices, enabled);
  const paymentMethod = useCachedResource('billing/payment-method', fetchPaymentMethod, enabled);

  const refetchAll = useCallback(() => {
    subscription.refetch();
    invoices.refetch();
    paymentMethod.refetch();
  }, [subscription, invoices, paymentMethod]);

  return { subscription, invoices, paymentMethod, refetchAll };
}
